import {
  Drawer,
  Box,
  Typography,
  TextField,
  MenuItem,
  Button,
  IconButton,
  Divider,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { useEffect, useState } from "react";
import { useApplicationContext } from "../../../context/ApplicationContext";
import { getReleaseFolderList_API } from "../../../api/controller/PlanningController";
import { useTheme } from "@mui/material/styles";
import { updateRegressionPlan_API } from "../../../api/controller/RegressionController";
import { Severity } from "../../../constant/ApplicationVariables";

const RegressionPlanDrawer = ({
  open,
  mode = "create",
  initialData,
  onClose,
  onSubmit,
  handleClose,
  onCreate,
  onUpdate,
  editingSuite,
  fetchRegressionPlans,
}) => {
  const { activeProject, handleSnackbar } = useApplicationContext();
  const theme = useTheme();

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [releaseName, setReleaseName] = useState("");
  const [releaseList, setReleaseList] = useState([]);

  useEffect(() => {
    if (mode === "edit" && initialData) {
      setName(initialData.name);
      setDescription(initialData.description);
      setReleaseName(initialData.releaseName);
    } else {
      setName("");
      setDescription("");
      setReleaseName("");
    }
  }, [mode, initialData]);

  useEffect(() => {
    if (!activeProject?.name || !open) return;

    const fetchReleases = async () => {
      const query = `name=${encodeURIComponent(activeProject.name)}`;
      const res = await getReleaseFolderList_API(query);
      if (res?.data) setReleaseList(res.data.map((r) => r.releaseName));
    };

    fetchReleases();
  }, [activeProject, open]);

  const handleSubmit = async () => {
    if (mode === "edit" && initialData) {
      // Update
      const payload = {
        projectName: activeProject.name,
        name: initialData.name, // existing plan
        newName: name !== initialData.name ? name : undefined,
        description:
          description !== initialData.description ? description : undefined,
        releaseName:
          releaseName !== initialData.releaseName ? releaseName : undefined,
      };

      try {
        await updateRegressionPlan_API(payload); // throws on failure
        fetchRegressionPlans(); // refresh parent list
        handleSnackbar(
          "Regression plan updated successfully",
          Severity.success
        );
        onClose(); // CLOSE drawer after success
      } catch (err) {
        console.error(err);
        handleSnackbar("Failed to update regression plan", Severity.error);
      }
    } else {
      // Create
      const payload = {
        projectName: activeProject.name,
        name,
        description,
        releaseName: releaseName || null,
      };

      try {
        await onSubmit(payload); // parent handles create API
        handleSnackbar(
          "Regression plan created successfully",
          Severity.success
        );
        onClose(); // close drawer after create
      } catch (err) {
        console.error(err);
        handleSnackbar("Failed to create regression plan", Severity.error);
      }
    }
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box
        sx={{
          width: 420,
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 2,
            display: "flex",
            justifyContent: "space-between",
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography fontWeight={600}>
            {mode === "edit"
              ? "Edit Regression Plan"
              : "Create Regression Plan"}
          </Typography>
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </Box>

        {/* Body */}
        <Box
          sx={{
            flex: 1,
            p: 3,
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <TextField
            label="Name"
            fullWidth
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <TextField
            label="Description"
            multiline
            rows={4}
            fullWidth
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
          <TextField
            select
            label="Release Name"
            fullWidth
            value={releaseName}
            onChange={(e) => setReleaseName(e.target.value)}
          >
            {releaseList.map((r) => (
              <MenuItem key={r} value={r}>
                {r}
              </MenuItem>
            ))}
          </TextField>
        </Box>

        {/* Footer */}
        <Box
          sx={{
            p: 2,
            display: "flex",
            gap: 2,
            borderTop: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Button fullWidth variant="outlined" onClick={onClose}>
            Cancel
          </Button>
          <Button
            fullWidth
            variant="contained"
            disabled={!name.trim()}
            onClick={handleSubmit}
            sx={{
              fontWeight: 600,
              textTransform: "none",
              color: name.trim()
                ? "hsl(var(--primary-foreground))" // active text color
                : theme.palette.text.disabled, // disabled text color
              background: name.trim()
                ? `linear-gradient(90deg, hsl(var(--cyan)), hsl(var(--emerald)))`
                : "transparent", // no background when disabled
              boxShadow: name.trim()
                ? `0 4px 14px hsl(var(--cyan) / 0.35)`
                : "none",
              border: name.trim()
                ? "none"
                : `1px solid ${theme.palette.divider}`,
              "&:hover": name.trim()
                ? {
                    background: `linear-gradient(90deg, hsl(var(--cyan) / 0.9), hsl(var(--emerald) / 0.9))`,
                    boxShadow: `0 6px 20px hsl(var(--cyan) / 0.45)`,
                  }
                : {},
              "&:active": name.trim() ? { transform: "scale(0.98)" } : {},
            }}
          >
            {mode === "edit" ? "Update" : "Create"}
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

export default RegressionPlanDrawer;
